import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { Spinner } from './Feedback'
import styles from './Button.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Disables the button and shows progress without changing its width. */
  loading?: boolean
  fullWidth?: boolean
  /** Leading glyph. Sized in `em`, so it tracks the button's own text. */
  icon?: ReactNode
}

/**
 * `type` defaults to `button`. The HTML default is `submit`, which turns every
 * unlabelled button inside a form into an accidental submit.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    fullWidth = false,
    icon,
    children,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={Boolean(disabled) || loading}
      aria-busy={loading || undefined}
      className={cx(
        styles.button,
        styles[variant],
        styles[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className,
      )}
    >
      {/*
        The label stays in flow while loading and is only made invisible, so the
        button keeps its measured width and the row around it does not reflow.
      */}
      <span className={styles.content}>
        {icon !== undefined && icon !== null ? (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {children}
      </span>
      {loading ? (
        <span className={styles.pending}>
          <Spinner size="sm" />
        </span>
      ) : null}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon-only control has no visible text to name it. */
  label: string
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Marks the control as an active toggle — a pinned panel, a live filter. */
  pressed?: boolean
  children: ReactNode
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    variant = 'ghost',
    size = 'md',
    loading = false,
    pressed,
    children,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      title={label}
      {...rest}
      ref={ref}
      type={type}
      disabled={Boolean(disabled) || loading}
      aria-label={label}
      aria-busy={loading || undefined}
      aria-pressed={pressed}
      className={cx(
        styles.button,
        styles.iconButton,
        styles[variant],
        styles[size],
        pressed === true && styles.pressed,
        loading && styles.loading,
        className,
      )}
    >
      <span className={styles.content} aria-hidden="true">
        {children}
      </span>
      {loading ? (
        <span className={styles.pending}>
          <Spinner size="sm" />
        </span>
      ) : null}
    </button>
  )
})
